import { ConsentData } from '../components/GDPRConsentModal';

export interface StoredConsentData extends ConsentData {
  version: string;
  ipHash?: string;
  sessionId?: string;
  userAgent?: string;
}

export interface ConsentStatus {
  hasValidConsent: boolean;
  consentData?: StoredConsentData;
  expiresAt?: Date;
  daysUntilExpiry?: number;
  isExpired: boolean;
}

export interface ConsentLog {
  action: 'consent_given' | 'consent_withdrawn' | 'consent_expired';
  consentData: Partial<ConsentData>;
  timestamp: Date;
  ipHash?: string;
  sessionId?: string;
  userAgent?: string;
  version: string;
}

class ConsentManager {
  private readonly STORAGE_KEY = 'smart_storage_gdpr_consent';
  private readonly CONSENT_VERSION = '1.0';
  private readonly CONSENT_VALIDITY_MONTHS = 13; // DSGVO-Standard
  private readonly API_BASE_URL = '/api';

  /**
   * Speichert Einwilligung lokal und loggt im Backend
   */
  async saveConsent(consentData: ConsentData): Promise<boolean> {
    try {
      const storedData: StoredConsentData = {
        ...consentData,
        version: this.CONSENT_VERSION,
        ipHash: await this.generateSessionHash(),
        sessionId: this.generateSessionId(),
        userAgent: navigator.userAgent
      };

      // Lokal speichern
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedData));

      // Backend-Logging (optional, nicht blockierend)
      try {
        await this.logConsentToBackend('consent_given', storedData);
      } catch (error) {
        console.warn('Backend consent logging failed:', error);
        // Fortsetzung auch bei Backend-Fehler
      }

      return true;
    } catch (error) {
      console.error('Failed to save consent:', error);
      return false;
    }
  }

  /**
   * Prüft gültige Einwilligung
   */
  getValidConsent(): ConsentStatus {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return {
          hasValidConsent: false,
          isExpired: false
        };
      }

      const consentData: StoredConsentData = JSON.parse(stored);

      // Version prüfen
      if (consentData.version !== this.CONSENT_VERSION) {
        return {
          hasValidConsent: false,
          isExpired: true
        };
      }

      // Ablaufzeit berechnen
      const consentDate = new Date(consentData.timestamp);
      const expiryDate = new Date(consentDate);
      expiryDate.setMonth(expiryDate.getMonth() + this.CONSENT_VALIDITY_MONTHS);

      const now = new Date();
      const isExpired = now > expiryDate;

      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (isExpired) {
        // Abgelaufene Einwilligung automatisch löschen
        this.clearConsent();
        return {
          hasValidConsent: false,
          isExpired: true
        };
      }

      return {
        hasValidConsent: true,
        consentData,
        expiresAt: expiryDate,
        daysUntilExpiry: Math.max(0, daysUntilExpiry),
        isExpired: false
      };
    } catch (error) {
      console.error('Failed to get consent status:', error);
      return {
        hasValidConsent: false,
        isExpired: false
      };
    }
  }

  /**
   * Widerruft Einwilligung
   */
  async withdrawConsent(): Promise<boolean> {
    try {
      const currentConsent = this.getValidConsent();

      // Backend-Logging für Widerruf
      if (currentConsent.consentData) {
        try {
          await this.logConsentToBackend('consent_withdrawn', currentConsent.consentData);
        } catch (error) {
          console.warn('Backend withdrawal logging failed:', error);
        }
      }

      // Lokal löschen
      localStorage.removeItem(this.STORAGE_KEY);

      return true;
    } catch (error) {
      console.error('Failed to withdraw consent:', error);
      return false;
    }
  }

  /**
   * Komplett-Löschung der Einwilligung
   */
  clearConsent(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear consent:', error);
    }
  }

  /**
   * Prüft ob spezifische Berechtigung gegeben ist
   */
  hasPermission(permission: 'required' | 'analytics' | 'marketing'): boolean {
    const status = this.getValidConsent();
    if (!status.hasValidConsent || !status.consentData) {
      return false;
    }

    return status.consentData[permission] === true;
  }

  /**
   * Prüft ob Einwilligung bald abläuft (innerhalb X Tage)
   */
  isConsentExpiringWithin(days: number = 30): boolean {
    const status = this.getValidConsent();
    if (!status.hasValidConsent || !status.daysUntilExpiry) {
      return false;
    }

    return status.daysUntilExpiry <= days;
  }

  /**
   * Generiert Session-Hash für Backend-Logging (Privacy-preserving)
   */
  private async generateSessionHash(): Promise<string> {
    try {
      // Verwende Browser-Fingerprint statt IP-Adresse
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().toDateString() // Tagesgranularität für Anonymität
      ].join('|');

      // Simple Hash-Funktion (in Produktion sollte crypto.subtle verwendet werden)
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 32-bit integer
      }

      return Math.abs(hash).toString(16);
    } catch (error) {
      console.warn('Failed to generate session hash:', error);
      return 'anonymous';
    }
  }

  /**
   * Generiert anonyme Session-ID
   */
  private generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  /**
   * Backend-Logging für Compliance (optional)
   */
  private async logConsentToBackend(action: ConsentLog['action'], consentData: StoredConsentData): Promise<void> {
    try {
      const logData: ConsentLog = {
        action,
        consentData: {
          required: consentData.required,
          analytics: consentData.analytics,
          marketing: consentData.marketing,
          timestamp: consentData.timestamp
        },
        timestamp: new Date(),
        ipHash: consentData.ipHash,
        sessionId: consentData.sessionId,
        userAgent: consentData.userAgent,
        version: consentData.version
      };

      const response = await fetch(`${this.API_BASE_URL}/consent/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (!response.ok) {
        throw new Error(`Backend logging failed: ${response.status}`);
      }
    } catch (error) {
      // Backend-Logging ist optional und darf Frontend nicht blockieren
      console.warn('Backend consent logging failed:', error);
      throw error; // Re-throw für Caller-Information
    }
  }

  /**
   * Exportiert alle gespeicherten Consent-Daten (DSGVO Art. 20)
   */
  exportConsentData(): string {
    try {
      const status = this.getValidConsent();
      if (!status.hasValidConsent) {
        return JSON.stringify({ message: 'Keine gültige Einwilligung gefunden' }, null, 2);
      }

      const exportData = {
        ...status.consentData,
        status: {
          hasValidConsent: status.hasValidConsent,
          expiresAt: status.expiresAt,
          daysUntilExpiry: status.daysUntilExpiry,
          isExpired: status.isExpired
        },
        exportedAt: new Date().toISOString(),
        note: 'Export gemäß DSGVO Art. 20 (Recht auf Datenübertragbarkeit)'
      };

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Failed to export consent data:', error);
      return JSON.stringify({ error: 'Export fehlgeschlagen' }, null, 2);
    }
  }

  /**
   * Debugging-Informationen für Support
   */
  getDebugInfo(): object {
    try {
      const status = this.getValidConsent();
      return {
        version: this.CONSENT_VERSION,
        validityMonths: this.CONSENT_VALIDITY_MONTHS,
        storageKey: this.STORAGE_KEY,
        hasLocalStorage: typeof Storage !== 'undefined',
        status,
        browserInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled
        }
      };
    } catch (error) {
      return { error: 'Debug info generation failed', details: error };
    }
  }
}

// Singleton-Export
export const consentManager = new ConsentManager();
export default ConsentManager;